/* eslint-disable sonarjs/cognitive-complexity */

/* eslint-disable @typescript-eslint/no-magic-numbers */

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import { Inject, Injectable, Optional } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { DateTime, Info } from 'luxon';

/** Creates an array and fills it with values. */
function range<T>(length: number, valueFunction: (index: number) => T): T[] {
  const valuesArray = Array.from<T>({ length });
  for (let i = 0; i < length; i++) {
    valuesArray[i] = valueFunction(i);
  }
  return valuesArray;
}
/** Adds luxon support to Angular Material. */
@Injectable()
export class LuxonAdapter extends DateAdapter<DateTime> {
  constructor(@Optional() @Inject(MAT_DATE_LOCALE) matDateLocale: string) {
    super();
    this.setLocale(matDateLocale || DateTime.local().locale);
  }

  public getYear(date: DateTime): number {
    return date.year;
  }

  public getMonth(date: DateTime): number {
    return date.month - 1;
  }

  public getDate(date: DateTime): number {
    return date.day;
  }

  public override getHours(date: DateTime): number {
    return date.hour;
  }

  public override getMinutes(date: DateTime): number {
    return date.minute;
  }

  public toMillis(date: DateTime): number {
    return date.toMillis();
  }

  public getDayOfWeek(date: DateTime): number {
    return date.weekday - 1;
  }

  public getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    return Info.months(style, { locale: this.locale });
  }

  public getDateNames(): string[] {
    return range(31, (i) => DateTime.local(DateTime.now().year, 1, i + 1, { locale: this.locale }).toFormat('d'));
  }

  public getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    return Info.weekdays(style, { locale: this.locale });
  }

  public getYearName(date: DateTime): string {
    return date.toFormat('y');
  }

  public getFirstDayOfWeek(): number {
    return 0;
  }

  public getNumDaysInMonth(date: DateTime): number {
    return date.daysInMonth || 0;
  }

  public clone(date: DateTime): DateTime {
    return date;
  }

  public createDate(year: number, month: number, date: number, hour = 0, minute = 0): DateTime {
    return DateTime.local(year, month + 1, date, hour, minute, { locale: this.locale });
  }

  public today(): DateTime {
    return DateTime.local({ locale: this.locale });
  }

  public parse(value: unknown, parseFormat: string | string[]): DateTime | null {
    if (typeof value == 'string' && value.length > 0) {
      const iso8601Date = DateTime.fromISO(value);

      if (this.isValid(iso8601Date)) {
        return iso8601Date;
      }

      const formats = Array.isArray(parseFormat) ? parseFormat : [parseFormat];

      if (parseFormat.length === 0) {
        throw new Error('Formats array must not be empty.');
      }

      for (const format of formats) {
        const fromFormat = DateTime.fromFormat(value, format);

        if (this.isValid(fromFormat)) {
          return fromFormat;
        }
      }

      return this.invalid();
    } else if (typeof value === 'number') {
      return DateTime.fromMillis(value);
    } else if (value instanceof Date) {
      return DateTime.fromJSDate(value);
    } else if (value instanceof DateTime) {
      return DateTime.fromMillis(value.toMillis());
    }

    return null;
  }

  public format(date: DateTime, displayFormat: string): string {
    if (!this.isValid(date)) {
      throw new Error('LuxonAdapter: Cannot format invalid date.');
    }

    return date.toFormat(displayFormat);
  }

  public addCalendarYears(date: DateTime, years: number): DateTime {
    return date.plus({ years });
  }

  public addCalendarMonths(date: DateTime, months: number): DateTime {
    return date.plus({ months });
  }

  public addCalendarDays(date: DateTime, days: number): DateTime {
    return date.plus({ days });
  }

  public toIso8601(date: DateTime): string {
    return date.toISO()!;
  }

  /**
   * Returns the given value if given a valid Date or null. Deserializes valid ISO 8601 strings
   * (https://www.ietf.org/rfc/rfc3339.txt) into valid Dates and empty string into null. Returns an
   * invalid date for all other values.
   */
  public override deserialize(value: unknown): DateTime | null {
    let date: DateTime | undefined;

    if (value instanceof Date) {
      date = DateTime.fromJSDate(value);
    }

    if (typeof value === 'string') {
      if (!value) {
        return null;
      }
      date = DateTime.fromISO(value);
    }

    if (date && this.isValid(date)) {
      return date;
    }

    return super.deserialize(value);
  }

  public isDateInstance(obj: object): boolean {
    return obj && obj instanceof DateTime;
  }

  public isValid(date: DateTime): boolean {
    return date.isValid;
  }

  public invalid(): DateTime {
    return DateTime.invalid('Invalid Luxon DateTime object.');
  }
}
